import { FabricImage, FabricObject, Group } from 'fabric';
import { ObjectTypeEnum } from '../../_enums/object-type.enum';
import { isSerializedImageType } from './fabric-object.enricher';
import { normalizeMnemonicEllipseDimensions } from '../shapes/mnemonic-ellipse.util';
import { applyMnemonicObjectBehavior } from '../shapes/mnemonic-object-behavior.util';
import { resolveMnemonicShadowFromJson } from '../shapes/mnemonic-shadow.util';

/**
 * Fabric 6 used originX/originY 'left'/'top' by default.
 * Fabric 7 defaults to 'center'/'center', which breaks groups saved without explicit origin.
 */
export function applyLegacyOriginDefaults(jsonObj: Record<string, unknown>, fabricObj: FabricObject): void {
    if (jsonObj.originX === undefined && jsonObj.originY === undefined) {
        fabricObj.set({ originX: 'left', originY: 'top' });
    }
}

export async function fabricObjectLoadReviver(
    jsonObj: Record<string, unknown>,
    fabricObj?: FabricObject,
    error?: unknown,
): Promise<FabricObject | void> {
    if (error) {
        if (isSerializedImageType(jsonObj) && typeof jsonObj.src === 'string' && jsonObj.src) {
            try {
                const { type, filters, resizeFilter, ...imageOptions } = jsonObj as Record<string, unknown>;
                const image = await FabricImage.fromURL(jsonObj.src, {}, imageOptions as any);
                applyLegacyOriginDefaults(jsonObj, image);
                return image;
            } catch (fallbackError) {
                console.warn('[mnemonic] Image fallback failed:', jsonObj.src, fallbackError);
            }
        }

        console.warn('[mnemonic] Failed to load canvas object:', jsonObj?.type, jsonObj, error);
        return;
    }

    if (fabricObj) {
        applyLegacyOriginDefaults(jsonObj, fabricObj);

        const restoredShadow = resolveMnemonicShadowFromJson(jsonObj.shadow);
        if (restoredShadow) {
            fabricObj.set('shadow', restoredShadow);
        }

        if (isSerializedImageType(jsonObj)) {
            fabricObj.set({
                id: jsonObj.id,
                imageFileId: jsonObj.imageFileId,
                objectType: jsonObj.objectType,
                name: jsonObj.name,
                text: jsonObj.text,
                tooltip: jsonObj.tooltip,
            } as any);
        }

        if (jsonObj.type === ObjectTypeEnum.gauge || jsonObj.objectType === ObjectTypeEnum.gauge) {
            fabricObj.set({
                id: jsonObj.id,
                name: jsonObj.name,
                objectType: ObjectTypeEnum.gauge,
                tooltip: jsonObj.tooltip,
                deviceId: jsonObj.deviceId,
                variableId: jsonObj.variableId,
                selectOption: jsonObj.selectOption,
                gauge: jsonObj.gauge,
            } as any);
        }

        if (
            jsonObj.variableId &&
            (jsonObj.objectType === ObjectTypeEnum.text ||
                jsonObj.type === 'Text' ||
                jsonObj.type === 'IText' ||
                jsonObj.type === 'i-text')
        ) {
            const textObject = fabricObj as any;

            if (textObject.text == null) {
                textObject.text = '';
            }
        }

        if (jsonObj.type === ObjectTypeEnum.chart || jsonObj.objectType === ObjectTypeEnum.chart) {
            fabricObj.set({
                id: jsonObj.id,
                name: jsonObj.name,
                objectType: ObjectTypeEnum.chart,
                tooltip: jsonObj.tooltip,
                deviceId: jsonObj.deviceId,
                variableId: jsonObj.variableId,
                selectOption: jsonObj.selectOption,
                chart: jsonObj.chart,
            } as any);
        }

        if (jsonObj.type === 'ellipse' || jsonObj.objectType === ObjectTypeEnum.ellipse) {
            normalizeMnemonicEllipseDimensions(fabricObj as any);
        }

        return fabricObj;
    }
}

const GROUP_PART_TYPES = new Set(['line', 'triangle']);

function normalizeGroupChildrenOrigins(object: FabricObject): void {
    if (!(object instanceof Group)) {
        return;
    }

    object.getObjects().forEach((child) => {
        if (GROUP_PART_TYPES.has(child.type)) {
            child.set({ originX: 'left', originY: 'top' });
        }

        normalizeGroupChildrenOrigins(child);
        child.setCoords();
    });

    object.setCoords();
}

/** loadFromJSON reviver is not applied to nested group children in Fabric 7. */
export function normalizeFabricCanvasAfterLoad(canvas: { getObjects(): FabricObject[] }): void {
    canvas.getObjects().forEach((object) => {
        normalizeGroupChildrenOrigins(object);
        normalizeMnemonicEllipseDimensions(object as any);
        applyMnemonicObjectBehavior(object);
        object.setCoords();
    });
}

export const fabricObjectLoadReviverFn = fabricObjectLoadReviver as (
    serializedObj: Record<string, unknown>,
    instance: FabricObject,
    error?: unknown,
) => Promise<FabricObject | void>;

export const applyLegacyOriginDefaultsFn = applyLegacyOriginDefaults as (serializedObj: Record<string, unknown>, instance: unknown) => void;
